/**
 * Experimental QR scanner using the BarcodeDetector API (Chrome Android).
 * Falls back with a clear message on unsupported browsers — M0.1 acceptance can
 * still be done by typing the pairing code manually.
 */
import { useEffect, useRef, useState } from 'react';

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

function detectorCtor(): BarcodeDetectorCtor | null {
  const candidate = (globalThis as unknown as { BarcodeDetector?: BarcodeDetectorCtor })
    .BarcodeDetector;
  return typeof candidate === 'function' ? candidate : null;
}

export function isQrScanSupported(): boolean {
  return detectorCtor() !== null && typeof navigator !== 'undefined' && 'mediaDevices' in navigator;
}

function extractCode(raw: string): string | null {
  // Raw pairing code.
  if (/^[A-Za-z2-9]{4}[- ]?[A-Za-z2-9]{4}$/u.test(raw.trim())) return raw.trim();
  try {
    const url = new URL(raw, window.location.href);
    const fromQuery = url.searchParams.get('code');
    if (fromQuery) return fromQuery;
    const hashMatch = /[?&]code=([^&]+)/u.exec(url.hash);
    if (hashMatch?.[1]) return decodeURIComponent(hashMatch[1]);
  } catch {
    return null;
  }
  return null;
}

export interface QrScannerProps {
  onResult(code: string): void;
  onCancel(): void;
}

export function QrScanner({ onResult, onCancel }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState<string | null>(
    isQrScanSupported()
      ? null
      : '当前浏览器不支持扫码（BarcodeDetector 不可用），请手动输入配对 code',
  );

  useEffect(() => {
    if (error) return undefined;
    let cancelled = false;
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const Detector = detectorCtor();
    if (!Detector) return undefined;
    const detector = new Detector({ formats: ['qr_code'] });

    const scan = async (): Promise<void> => {
      const video = videoRef.current;
      if (!video || cancelled || video.readyState < 2) return;
      try {
        const codes = await detector.detect(video);
        const raw = codes[0]?.rawValue;
        if (raw) {
          const code = extractCode(raw);
          if (code) {
            cancelled = true;
            onResult(code);
            return;
          }
        }
      } catch {
        /* transient detect errors are ignored */
      }
      if (!cancelled) timer = setTimeout(() => void scan(), 350);
    };

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        timer = setTimeout(() => void scan(), 300);
      } catch (cameraError) {
        if (!cancelled) {
          setError(
            cameraError instanceof Error
              ? `无法打开摄像头：${cameraError.message}`
              : '无法打开摄像头（需要 HTTPS 与摄像头权限）',
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [error, onResult]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90 p-6">
      <div className="flex items-center justify-between text-slate-200">
        <span className="text-sm font-medium">扫描 PC 端配对二维码</span>
        <button
          type="button"
          className="rounded-lg border border-white/20 px-3 py-1 text-xs"
          onClick={onCancel}
        >
          取消
        </button>
      </div>
      <div className="relative mt-6 flex-1 overflow-hidden rounded-2xl border border-white/10 bg-black">
        {error ? (
          <div className="flex h-full items-center justify-center p-6 text-center text-sm text-amber-200">
            {error}
          </div>
        ) : (
          <video ref={videoRef} playsInline muted className="h-full w-full object-cover" />
        )}
        {!error ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-52 w-52 rounded-2xl border-2 border-indigo-400/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
          </div>
        ) : null}
      </div>
      <p className="mt-4 text-center text-xs text-slate-400">
        将 PC 上显示的二维码放入框内；不支持扫码时可手动输入 code。
      </p>
    </div>
  );
}
